<template>
  <el-image
    ref="elImage"
    :src="displaySrc"
    :preview-src-list="resolvedPreviewList"
    v-bind="forwardedAttrs"
    v-on="$listeners"
    @click.native="handleBrowseClick"
  >
    <template v-for="(_, slotName) in $scopedSlots" :slot="slotName" slot-scope="scope">
      <slot :name="slotName" v-bind="scope" />
    </template>
  </el-image>
</template>

<script>
import {
  resolveDisplayMediaUrl,
  resolveMediaUrl,
  batchResolveOssMediaUrls,
  stripOssUrlQuery,
  isAliyunOssHttpsUrl,
  lookupCachedOssSignedUrl,
} from '../utils/mediaUrl'
import { getUploadSchoolId } from '../utils/ossMediaUpload'

/**
 * el-image 封装：展示与点击预览时自动解析 OSS 预签名（带缓存）。
 * 详情页/弹窗内直接传原始路径即可，无需在 openDetail 里单独预取。
 * 最后更新：2026-07-01
 */
export default {
  name: 'OssElImage',
  inheritAttrs: false,
  props: {
    src: { type: String, default: '' },
    schoolId: { type: [String, Number], default: '' },
    inline: { type: Boolean, default: true },
    /** 对应 el-image preview-src-list，传原始路径数组 */
    previewSrcList: { type: Array, default: () => [] },
    /** true：仅在本组件被点击浏览时再请求 OSS 预签名（缩略图不主动签） */
    lazy: { type: Boolean, default: false },
  },
  data() {
    const initialSrc = this.src ? String(this.src).trim() : ''
    return {
      displaySrc: initialSrc && !isAliyunOssHttpsUrl(initialSrc) ? resolveMediaUrl(initialSrc) : '',
      resolvedPreviewList: [],
      browseResolved: false,
      resolvingBrowse: false,
    }
  },
  computed: {
    forwardedAttrs() {
      const attrs = { ...this.$attrs }
      delete attrs['preview-src-list']
      return attrs
    },
    effectiveSchoolId() {
      return this.schoolId || getUploadSchoolId(this)
    },
  },
  watch: {
    src: {
      immediate: true,
      handler(val) {
        this.loadDisplaySrc(val)
      },
    },
    previewSrcList: {
      deep: true,
      immediate: true,
      handler() {
        if (!this.lazy || this.browseResolved) {
          this.resolvePreviewList()
        } else {
          this.resolvedPreviewList = []
        }
      },
    },
    schoolId() {
      this.browseResolved = false
      this.loadDisplaySrc(this.src)
      this.resolvePreviewList()
    },
    lazy() {
      this.browseResolved = false
      this.loadDisplaySrc(this.src)
      if (!this.lazy) this.resolvePreviewList()
      else this.resolvedPreviewList = []
    },
  },
  methods: {
    syncDisplayUrl(path) {
      const p = stripOssUrlQuery(String(path || '').trim())
      if (!p) return ''
      if (!isAliyunOssHttpsUrl(p)) return resolveMediaUrl(p)
      const hit = this.effectiveSchoolId
        ? lookupCachedOssSignedUrl(this.effectiveSchoolId, p, this.inline)
        : null
      return hit || ''
    },
    async loadDisplaySrc(path) {
      const p = path == null ? '' : String(path).trim()
      if (!p) {
        this.displaySrc = ''
        return
      }
      const stable = stripOssUrlQuery(p)
      if (this.lazy && isAliyunOssHttpsUrl(stable) && !this.browseResolved) {
        this.displaySrc = this.syncDisplayUrl(p)
        return
      }
      try {
        this.displaySrc = await resolveDisplayMediaUrl(p, this.effectiveSchoolId, { inline: this.inline })
      } catch (e) {
        console.warn('[OssElImage]', e)
        this.displaySrc = resolveMediaUrl(p)
      }
    },
    async resolvePreviewList() {
      const list = Array.isArray(this.previewSrcList) ? this.previewSrcList : []
      if (!list.length) {
        this.resolvedPreviewList = []
        return
      }
      const schoolId = this.effectiveSchoolId
      const resolved = new Array(list.length)
      const pending = []
      const pendingIndex = []

      list.forEach((path, index) => {
        const stable = stripOssUrlQuery(String(path || '').trim())
        if (!stable) return
        if (!isAliyunOssHttpsUrl(stable)) {
          resolved[index] = resolveMediaUrl(stable)
          return
        }
        const hit = schoolId ? lookupCachedOssSignedUrl(schoolId, stable, this.inline) : null
        if (hit) resolved[index] = hit
        else {
          pending.push(stable)
          pendingIndex.push(index)
        }
      })

      if (pending.length && schoolId) {
        try {
          const batch = await batchResolveOssMediaUrls(pending, schoolId, { inline: this.inline })
          pending.forEach((stable, i) => {
            const idx = pendingIndex[i]
            if (batch[stable]) resolved[idx] = batch[stable]
          })
        } catch (e) {
          console.warn('[OssElImage] preview', e)
        }
      }

      this.resolvedPreviewList = list
        .map((path, index) => resolved[index] || resolveMediaUrl(path))
        .filter(Boolean)
    },
    async ensureBrowseReady() {
      if (this.browseResolved || this.resolvingBrowse) return
      const p = String(this.src || '').trim()
      const stable = stripOssUrlQuery(p)
      const previewList = Array.isArray(this.previewSrcList) ? this.previewSrcList : []
      const needResolve =
        (p && isAliyunOssHttpsUrl(stable)) ||
        previewList.some((item) => isAliyunOssHttpsUrl(stripOssUrlQuery(String(item || '').trim())))

      if (!needResolve) {
        this.browseResolved = true
        return
      }

      this.resolvingBrowse = true
      try {
        await Promise.all([this.loadDisplaySrc(this.src), this.resolvePreviewList()])
        this.browseResolved = true
      } finally {
        this.resolvingBrowse = false
      }
    },
    async handleBrowseClick(e) {
      if (!this.lazy) return
      const hadSignedSrc = !!this.displaySrc
      await this.ensureBrowseReady()
      if (!hadSignedSrc && this.displaySrc && this.$refs.elImage) {
        e.stopImmediatePropagation()
        this.$nextTick(() => {
          const root = this.$refs.elImage.$el
          if (root) root.click()
        })
      }
    },
  },
}
</script>
